import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import useSettings from '../../utils/useSettings';
import Logo from '../Logo';
import { useTranslation } from 'react-i18next';
import safeStorage from '../../utils/safeStorage';

const LANG_OPTIONS = [
  { code: 'en', flag: '🇬🇧', label: 'English' },
  { code: 'es', flag: '🇪🇸', label: 'Español' },
  { code: 'fr', flag: '🇫🇷', label: 'Français' },
  { code: 'de', flag: '🇩🇪', label: 'Deutsch' },
  { code: 'pt', flag: '🇧🇷', label: 'Português' },
  { code: 'it', flag: '🇮🇹', label: 'Italiano' },
  { code: 'hi', flag: '🇮🇳', label: 'हिन्दी' },
  { code: 'gu', flag: '🇮🇳', label: 'ગુજરાતી' },
  { code: 'ar', flag: '🇸🇦', label: 'العربية' },
  { code: 'zh', flag: '🇨🇳', label: '中文' },
  { code: 'ja', flag: '🇯🇵', label: '日本語' },
];

const Navbar = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { settings } = useSettings();
  const flags = settings.featureFlags || {};
  const { t, i18n } = useTranslation();
  const { darkMode, toggleDarkMode } = useTheme();

  const currentLang = LANG_OPTIONS.find(l => l.code === i18n.language) || LANG_OPTIONS[0];

  const handleLangChange = (code) => {
    i18n.changeLanguage(code);
    safeStorage.setItem('dreamyLang', code);
  };

  const navLinks = [
    flags.community && { to: '/community', label: t('navbar.blog') === 'Blog' ? '🌍 Community' : '🌍 ' + t('nav.community') },
    { to: '/blog',      label: t('navbar.blog') },
    { to: '/about',     label: t('navbar.about') },
    { to: '/faq',       label: t('navbar.faq') },
    { to: '/pricing',   label: t('navbar.pricing') },
    { to: '/affiliate', label: t('navbar.affiliates') },
    flags.giftStory && { to: '/gift', label: t('navbar.giftStory') },
  ].filter(Boolean);

  return (
    <nav className="navbar navbar-expand-lg sticky-top" style={{
        background: darkMode ? '#110d2a' : '#fff',
        boxShadow: darkMode ? '0 2px 16px rgba(0,0,0,0.3)' : '0 2px 16px rgba(45,27,105,0.08)',
        borderBottom: darkMode ? '1px solid #2d1b69' : '1px solid #f0eaf8',
      }}>
      <div className="container">

        {/* Brand */}
        <Link className="navbar-brand text-decoration-none" to="/">
          <Logo size={36} variant="full" />
        </Link>

        {/* Mobile toggle */}
        <button
          className="navbar-toggler border-0 shadow-none"
          type="button"
          onClick={() => setOpen(o => !o)}
          style={{color:'#6f42c1'}}>
          <i className={`bi ${open ? 'bi-x-lg' : 'bi-list'} fs-4`}></i>
        </button>

        {/* Links */}
        <div className={`collapse navbar-collapse ${open ? 'show' : ''}`}>
          <ul className="navbar-nav mx-auto align-items-lg-center gap-1">
            {navLinks.map(link => (
              <li className="nav-item" key={link.to}>
                <Link
                  className="nav-link px-3 fw-semibold"
                  to={link.to}
                  onClick={() => setOpen(false)}
                  style={{
                    color: location.pathname === link.to ? '#6f42c1' : (darkMode ? '#c9a8ff' : '#555'),
                    borderRadius: '8px',
                    fontSize: '15px',
                  }}>
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>

          <ul className="navbar-nav align-items-lg-center gap-2">
            {/* Dark mode toggle */}
            <li className="nav-item">
              <button
                onClick={toggleDarkMode}
                className="btn btn-link nav-link px-2 d-flex align-items-center"
                title={darkMode ? 'Switch to light mode' : 'Switch to night mode'}
                style={{color: darkMode ? '#f9c74f' : '#555', textDecoration:'none', fontSize:'18px'}}>
                <i className={`bi ${darkMode ? 'bi-sun-fill' : 'bi-moon-stars-fill'}`}></i>
              </button>
            </li>
            {/* Language switcher */}
            <li className="nav-item dropdown">
              <button
                className="btn btn-link nav-link px-2 dropdown-toggle d-flex align-items-center gap-1"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style={{color: darkMode ? '#c9a8ff' : '#555', fontSize:'14px', textDecoration:'none'}}>
                <span>{currentLang.flag}</span>
                <span className="d-none d-lg-inline" style={{fontSize:'13px'}}>{currentLang.code.toUpperCase()}</span>
              </button>
              <ul className="dropdown-menu dropdown-menu-end shadow-sm border-0" style={{borderRadius:'12px', minWidth:'160px', padding:'8px'}}>
                {LANG_OPTIONS.map(lang => (
                  <li key={lang.code}>
                    <button
                      className="dropdown-item rounded-2 d-flex align-items-center gap-2"
                      style={{
                        fontSize:'14px',
                        fontWeight: lang.code === i18n.language ? 700 : 400,
                        color: lang.code === i18n.language ? '#6f42c1' : 'var(--text-body)',
                        padding:'6px 12px',
                      }}
                      onClick={() => { handleLangChange(lang.code); setOpen(false); }}>
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                      {lang.code === i18n.language && (
                        <i className="bi bi-check2 ms-auto" style={{color:'#6f42c1'}}></i>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </li>

            {user ? (
              <>
                <li className="nav-item">
                  <span className="nav-link d-flex align-items-center gap-2" style={{color:'var(--text-soft)', fontSize:'14px'}}>
                    <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                      style={{width:'30px', height:'30px', background:'#6f42c1', fontSize:'13px', flexShrink:0}}>
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                    <span style={{color:'var(--text-main)', fontWeight:600}}>{user.name?.split(' ')[0]}</span>
                  </span>
                </li>
                <li className="nav-item">
                  <Link className="btn btn-dream rounded-pill px-4 fw-semibold" to="/dashboard" onClick={() => setOpen(false)}
                    style={{fontSize:'14px'}}>
                    <i className="bi bi-house-heart me-1"></i>{t('navbar.dashboard')}
                  </Link>
                </li>
              </>
            ) : (
              <>
                <li className="nav-item">
                  <Link className="nav-link px-3 fw-semibold" to="/login" onClick={() => setOpen(false)}
                    style={{color:'var(--text-muted2)', fontSize:'15px'}}>
                    {t('navbar.login')}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="btn btn-dream rounded-pill px-4 fw-semibold" to="/register" onClick={() => setOpen(false)}
                    style={{fontSize:'14px'}}>
                    {t('navbar.register')}
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
